import React from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ShieldAlert,
  Sparkles,
} from "lucide-react";

const RISK = {
  low: {
    label: "Low risk",
    icon: CheckCircle2,
    chip: "bg-live-soft text-live",
  },
  medium: {
    label: "Medium risk",
    icon: AlertTriangle,
    chip: "bg-pending-soft text-pending",
  },
  high: {
    label: "High risk",
    icon: ShieldAlert,
    chip: "bg-opposed-soft text-opposed",
  },
  unknown: {
    label: "Not scored",
    icon: AlertTriangle,
    chip: "bg-ink-100 text-ink-600",
  },
};

/**
 * Clearance analysis panel. Sits beside the table on wide screens and above
 * it on narrow ones — it frames the results but never replaces reading them.
 */
export default function AIInsights({ insight, results = [] }) {
  if (!insight) return null;

  const risk = RISK[insight.overallRiskLevel] || RISK.unknown;
  const RiskIcon = risk.icon;
  const nameById = new Map(results.map((r) => [r.trademarkId, r.wordMark]));

  return (
    <aside className="overflow-hidden rounded-soft bg-ink-950 text-white">
      <div className="p-5">
        <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-semibold">
          <Sparkles className="h-4 w-4 text-[#8f5eeb]" aria-hidden="true" />
          Clearance analysis
        </h2>

        <span
          className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-bold ${risk.chip}`}
        >
          <RiskIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {risk.label}
        </span>

        {insight.summary && (
          <p className="mt-4 text-sm leading-relaxed text-ink-200">
            {insight.summary}
          </p>
        )}

        {insight.conflicts?.length > 0 && (
          <section className="mt-5 border-t border-white/10 pt-5">
            <h3 className="mb-3 text-micro font-semibold text-ink-400">
              Closest matches ({insight.conflicts.length})
            </h3>
            <ul className="space-y-2.5">
              {insight.conflicts.map((conflict, index) => (
                <li
                  key={conflict.trademarkId || index}
                  className="rounded bg-white/5 p-3 text-xs leading-relaxed"
                >
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <span className="truncate font-semibold text-white">
                      {nameById.get(conflict.trademarkId) ||
                        conflict.trademarkId}
                    </span>
                    <span className="shrink-0 font-bold text-[#8f5eeb]">
                      {conflict.similarityScore}%
                    </span>
                  </div>
                  <p className="text-ink-300">{conflict.reasoning}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {insight.recommendations?.length > 0 && (
          <section className="mt-5 border-t border-white/10 pt-5">
            <h3 className="mb-3 text-micro font-semibold text-ink-400">
              What to do next
            </h3>
            <ul className="space-y-2">
              {insight.recommendations.map((item, index) => (
                <li key={index} className="flex gap-2.5 text-sm text-ink-200">
                  <span
                    aria-hidden="true"
                    className="mt-[0.5em] h-1 w-1 shrink-0 rounded-full bg-[#8f5eeb]-400"
                  />
                  {item}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </aside>
  );
}
